import { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Flex,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Spinner,
  Text,
  useColorModeValue,
  FormErrorMessage,
  FormHelperText,
  useToast,
  SimpleGrid,
  Select,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import {
  apiCreateCarrier,
  apiGetCarrier,
  apiUpdateCarrier,
  apiGetActurisProductsByType,
} from 'api';

import type { APICarrierData, APIActurisProductsByType } from 'types/api.types';

import { PAF_TEMPLATE_OPTIONS_LEGACY, PAF_TEMPLATE_OPTIONS } from './constants';
import { PROVINCE_OPTIONS } from 'variables/options';

import { useFeatureFlag } from 'hooks/useFeatureFlag';

const CarrierFormView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const isPafGenerationEnabled = useFeatureFlag('enable-paf-generation');
  const isDashAutopullEnabled = useFeatureFlag('enable-dash-autopull');

  const isEditMode = !!id;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    is_dash: false,
    dash_requested_years: null as number | null,
    dash_province_years: [] as {
      province: number | null;
      dash_requested_years: number | null;
    }[],
    paf_template: null as string | null,
    csio_code: '',
    address: '',
    city: '',
    province: null as number | null,
    postal_code: '',
    province_configs: [] as {
      province: number | null;
      auto_product: string;
      auto_telematics_product: string;
      property_product: string;
    }[],
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch carrier data if in edit mode
  const {
    data: carrierData,
    isLoading: isLoadingCarrier,
    error: carrierError,
  } = useQuery<APICarrierData>({
    queryKey: ['carrier', id],
    queryFn: () => apiGetCarrier(parseInt(id)),
    enabled: isEditMode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch Acturis products by type for province configuration
  const { data: productsByType, isLoading: isLoadingProductsByType } =
    useQuery<APIActurisProductsByType>({
      queryKey: ['acturis-products-by-type'],
      queryFn: () => apiGetActurisProductsByType(),
      staleTime: 5 * 60 * 1000,
    });

  // Set form data when carrier data is loaded
  useEffect(() => {
    if (carrierData && isEditMode) {
      setFormData({
        name: carrierData.name || '',
        is_dash: carrierData.is_dash || false,
        dash_requested_years: carrierData.dash_requested_years || null,
        dash_province_years: (carrierData.dash_province_years || []).map(
          (row) => ({
            province: row.province,
            dash_requested_years: row.dash_requested_years,
          }),
        ),
        paf_template: carrierData.paf_template || null,
        csio_code: carrierData.csio_code || '',
        address: carrierData.address || '',
        city: carrierData.city || '',
        province: carrierData.province || null,
        postal_code: carrierData.postal_code || '',
        province_configs: (carrierData.province_configs || []).map((row) => ({
          province: row.province,
          auto_product: row.auto_product || '',
          auto_telematics_product: row.auto_telematics_product || '',
          property_product: row.property_product || '',
        })),
      });
    }
  }, [carrierData, isEditMode]);

  // Create carrier mutation
  const createCarrierMutation = useMutation({
    mutationFn: (data: any) => apiCreateCarrier(data),
    onSuccess: () => {
      toast({
        title: 'Carrier created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/admin/settings/carriers');
    },
    onError: handleApiError,
  });

  // Update carrier mutation
  const updateCarrierMutation = useMutation({
    mutationFn: ({ carrierId, data }: { carrierId: number; data: any }) =>
      apiUpdateCarrier(carrierId, data),
    onSuccess: () => {
      toast({
        title: 'Carrier updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      // Invalidate the carrier query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['carrier', id] });
      navigate('/admin/settings/carriers');
    },
    onError: handleApiError,
  });

  function getErrorMessage(value: any): string | null {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value) || (value && typeof value === 'object')) {
      const values = Array.isArray(value) ? value : Object.values(value);

      for (const entry of values) {
        const message = getErrorMessage(entry);

        if (message) {
          return message;
        }
      }
    }

    return null;
  }

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} carrier`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};
    const source =
      error.response && error.response.data
        ? error.response.data
        : typeof error === 'object'
          ? error
          : {};

    Object.entries(source).forEach(([field, messages]: [string, any]) => {
      const message = getErrorMessage(messages);

      if (message) {
        formattedErrors[field] = message;
      }
    });

    setErrors(formattedErrors);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'is_dash'
          ? value === 'true'
          : name === 'province'
            ? value === ''
              ? null
              : parseInt(value)
            : value === ''
              ? null
              : value,
    }));

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const addProvinceYear = () => {
    setFormData((prev) => ({
      ...prev,
      dash_province_years: [
        ...prev.dash_province_years,
        { province: null, dash_requested_years: null },
      ],
    }));
  };

  const removeProvinceYear = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      dash_province_years: prev.dash_province_years.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const updateProvinceYear = (
    index: number,
    field: 'province' | 'dash_requested_years',
    value: number | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      dash_province_years: prev.dash_province_years.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const addProvinceConfig = () => {
    setFormData((prev) => ({
      ...prev,
      province_configs: [
        ...prev.province_configs,
        {
          province: null,
          auto_product: '',
          auto_telematics_product: '',
          property_product: '',
        },
      ],
    }));
  };

  const removeProvinceConfig = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      province_configs: prev.province_configs.filter((_, i) => i !== index),
    }));
  };

  const updateProvinceConfig = (
    index: number,
    field:
      | 'province'
      | 'auto_product'
      | 'auto_telematics_product'
      | 'property_product',
    value: number | string | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province_configs: prev.province_configs.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim()) {
      setErrors({
        ...errors,
        name: 'Name is required',
      });
      return;
    }

    if (
      isDashAutopullEnabled &&
      formData.dash_province_years.some((row) => row.province === null)
    ) {
      setErrors({
        ...errors,
        dash_province_years: 'Select a province for each DASH years row.',
      });
      return;
    }

    if (formData.province_configs.some((row) => row.province === null)) {
      setErrors({
        ...errors,
        province_configs: 'Select a province for each Acturis product row.',
      });
      return;
    }

    try {
      const carrierPayload = {
        name: formData.name,
        is_dash: formData.is_dash,
        dash_requested_years: formData.dash_requested_years,
        ...(isDashAutopullEnabled && {
          dash_province_years: formData.dash_province_years.map((row) => ({
            province: row.province,
            dash_requested_years: row.dash_requested_years,
          })),
        }),
        paf_template: formData.paf_template,
        csio_code: formData.csio_code,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postal_code,
        province_configs: formData.province_configs
          .filter((row) => row.province !== null)
          .map((row) => ({
            province: row.province,
            auto_product: row.auto_product || '',
            auto_telematics_product: row.auto_telematics_product || '',
            property_product: row.property_product || '',
          })),
      };

      if (isEditMode && id) {
        // Update existing carrier
        updateCarrierMutation.mutate({
          carrierId: parseInt(id),
          data: carrierPayload,
        });
      } else {
        // Create new carrier
        createCarrierMutation.mutate(carrierPayload);
      }

      queryClient.invalidateQueries({ queryKey: ['carrier-list'] });
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} carrier`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Show loading state
  if (isEditMode && isLoadingCarrier) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if carrier data failed to load (in edit mode)
  if (isEditMode && carrierError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading carrier data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createCarrierMutation.isPending || updateCarrierMutation.isPending;

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit Carrier' : 'Create New Carrier'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={6}>
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              variant="main"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Carrier Name"
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.is_dash}>
            <FormLabel>Accepts DASH reports</FormLabel>
            <Select
              variant="main"
              name="is_dash"
              value={formData.is_dash.toString()}
              onChange={handleSelectChange}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
            <FormErrorMessage>{errors.is_dash}</FormErrorMessage>
          </FormControl>

          {formData.is_dash && (
            <FormControl isInvalid={!!errors.dash_requested_years}>
              <FormLabel>DASH Requested Years</FormLabel>
              <Input
                variant="main"
                name="dash_requested_years"
                type="number"
                value={formData.dash_requested_years || ''}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value)
                    : null;
                  setFormData((prev) => ({
                    ...prev,
                    dash_requested_years: value,
                  }));
                  // Clear any previous errors for this field
                  if (errors.dash_requested_years) {
                    setErrors((prev) => ({
                      ...prev,
                      dash_requested_years: '',
                    }));
                  }
                }}
                placeholder="Leave empty for maximum years"
              />
              <FormHelperText>
                Number of years of driving history to request. Leave empty to
                request maximum available years.
              </FormHelperText>
              <FormErrorMessage>{errors.dash_requested_years}</FormErrorMessage>
            </FormControl>
          )}

          {formData.is_dash && isDashAutopullEnabled && (
            <FormControl isInvalid={!!errors.dash_province_years}>
              <FormLabel>DASH Requested Years by Province</FormLabel>
              <FormHelperText mb={3}>
                Optional per-province overrides of the requested years above
              </FormHelperText>

              {formData.dash_province_years.map((row, index) => (
                <Flex key={index} gap={3} align="flex-end" mb={3}>
                  <Box flex="1">
                    <Select
                      variant="main"
                      value={row.province ?? ''}
                      onChange={(e) =>
                        updateProvinceYear(
                          index,
                          'province',
                          e.target.value === ''
                            ? null
                            : parseInt(e.target.value),
                        )
                      }
                      placeholder="Select Province"
                    >
                      {PROVINCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box flex="1">
                    <Input
                      variant="main"
                      type="number"
                      min={1}
                      value={row.dash_requested_years ?? ''}
                      onChange={(e) =>
                        updateProvinceYear(
                          index,
                          'dash_requested_years',
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      placeholder="Leave empty for maximum years"
                    />
                  </Box>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removeProvinceYear(index)}
                  >
                    Remove
                  </Button>
                </Flex>
              ))}

              <Button type="button" variant="outline" onClick={addProvinceYear}>
                Add Province Override
              </Button>
              <FormErrorMessage>{errors.dash_province_years}</FormErrorMessage>
            </FormControl>
          )}

          <FormControl isInvalid={!!errors.paf_template}>
            <FormLabel>PAF Template</FormLabel>
            <Select
              variant="main"
              name="paf_template"
              value={formData.paf_template || ''}
              onChange={handleSelectChange}
              placeholder="No Template"
            >
              {(isPafGenerationEnabled
                ? PAF_TEMPLATE_OPTIONS
                : PAF_TEMPLATE_OPTIONS_LEGACY
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.paf_template}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.csio_code}>
            <FormLabel>CSIO Carrier Code</FormLabel>
            <Input
              variant="main"
              name="csio_code"
              value={formData.csio_code}
              onChange={handleInputChange}
            />
            <FormHelperText>
              CSIO carrier code used for My Proof of Insurance (MPOI) eSlip
              generation
            </FormHelperText>
            <FormErrorMessage>{errors.csio_code}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.address}>
            <FormLabel>Address</FormLabel>
            <Input
              variant="main"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Street Address"
            />
            <FormErrorMessage>{errors.address}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.city}>
            <FormLabel>City</FormLabel>
            <Input
              variant="main"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="City"
            />
            <FormErrorMessage>{errors.city}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.province}>
            <FormLabel>Province</FormLabel>
            <Select
              variant="main"
              name="province"
              value={formData.province || ''}
              onChange={handleSelectChange}
              placeholder="Select Province"
            >
              {PROVINCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.province}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.postal_code}>
            <FormLabel>Postal Code</FormLabel>
            <Input
              variant="main"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              placeholder="Postal Code"
            />
            <FormErrorMessage>{errors.postal_code}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>
      </Card>

      <Card mb="20px">
        <Heading size="md" mb={2} color={textColor}>
          Acturis Products
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Configure which Acturis products this carrier uses in each province
        </Text>

        <FormControl isInvalid={!!errors.province_configs}>
          {formData.province_configs.map((row, index) => (
            <Box
              key={index}
              p={4}
              mb={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={borderColor}
            >
              <Flex gap={3} align="flex-end" mb={3}>
                <Box flex="1">
                  <FormLabel fontSize="sm" mb={1}>
                    Province
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={row.province ?? ''}
                    onChange={(e) =>
                      updateProvinceConfig(
                        index,
                        'province',
                        e.target.value === '' ? null : parseInt(e.target.value),
                      )
                    }
                    placeholder="Select Province"
                  >
                    {(productsByType?.supported_provinces || []).map(
                      (option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ),
                    )}
                  </Select>
                </Box>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => removeProvinceConfig(index)}
                >
                  Remove
                </Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <Box>
                  <FormLabel fontSize="sm" mb={1}>
                    Auto Product
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={row.auto_product}
                    onChange={(e) =>
                      updateProvinceConfig(
                        index,
                        'auto_product',
                        e.target.value,
                      )
                    }
                    placeholder="Select auto product"
                    isDisabled={isLoadingProductsByType}
                  >
                    {(productsByType?.auto || []).map((product) => (
                      <option key={product} value={product}>
                        {product}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <FormLabel fontSize="sm" mb={1}>
                    Auto Telematics
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={row.auto_telematics_product}
                    onChange={(e) =>
                      updateProvinceConfig(
                        index,
                        'auto_telematics_product',
                        e.target.value,
                      )
                    }
                    placeholder="None"
                    isDisabled={isLoadingProductsByType}
                  >
                    {(productsByType?.auto_telematics || []).map((product) => (
                      <option key={product} value={product}>
                        {product}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <FormLabel fontSize="sm" mb={1}>
                    Property Product
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={row.property_product}
                    onChange={(e) =>
                      updateProvinceConfig(
                        index,
                        'property_product',
                        e.target.value,
                      )
                    }
                    placeholder="Select property product"
                    isDisabled={isLoadingProductsByType}
                  >
                    {(productsByType?.property || []).map((product) => (
                      <option key={product} value={product}>
                        {product}
                      </option>
                    ))}
                  </Select>
                </Box>
              </SimpleGrid>
            </Box>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addProvinceConfig}
            isDisabled={isLoadingProductsByType}
          >
            Add Province
          </Button>
          <FormErrorMessage>{errors.province_configs}</FormErrorMessage>
        </FormControl>
      </Card>

      <Flex justify="flex-end">
        <Button
          variant="outline"
          mr={3}
          onClick={() => navigate('/admin/settings/carriers')}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="brand"
          type="submit"
          isLoading={isSubmitting}
          loadingText={isEditMode ? 'Updating' : 'Creating'}
        >
          {isEditMode ? 'Update Carrier' : 'Create Carrier'}
        </Button>
      </Flex>
    </form>
  );
};

export default CarrierFormView;
